// Torneo.tsx — /t/<codice>: l'invito, la sala d'attesa (4 posti), il tabellone, la mia partita, il vincitore.
// Lo stato lo decide il server: qui si chiede ogni pochi secondi e si mostra. Quando tocca a me, si entra nella
// partita (una sfida normale su /c/<codice>, con il token già dato dal torneo).
import { InvitaAmici } from "../componenti/InvitaAmici.tsx";
import { Palco } from "../componenti/Palco.tsx";
import { Tutorial, tutorialVisto } from "../componenti/Tutorial.tsx";
import { useAccount } from "../servizi/account.ts";
import { PulsanteBot } from "../prova/PulsanteBot.tsx";
import { AvvisoSospensione } from "../componenti/AvvisoSospensione.tsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Faccina } from "../componenti/Faccina.tsx";
import { Avatar } from "../componenti/Avatar.tsx";
import { CampoNome, useNomeGiocatore } from "../gioco/CampoNome.tsx";
import { Condividi } from "../gioco/Condividi.tsx";
import { ErroreRete } from "../servizi/api.ts";
import { salvaPreferenza } from "../servizi/preferenze.ts";
import { vai } from "../servizi/percorso.ts";
import {
  annullaTorneo,
  dimenticaTorneo,
  entraTorneo,
  esciTorneo,
  aggiungiBotTorneo,
  iniziaTorneo,
  leggiTorneo,
  linkTorneo,
  preparaPartita,
  salvaTokenTorneo,
  statoTorneo,
  tokenTorneo,
  type InfoTorneo,
  type Turno,
  type VistaTorneo,
} from "../servizi/tornei.ts";
import type { ControlloTema } from "../temi/useTema.ts";
import { IMP_APP, PERCORSO_SFIDA, PERCORSO_TORNEO, PREFERENZE, TORNEO_AGGIORNA_SECONDI } from "../costanti.ts";
import { eNumero, leggiImpostazione } from "../servizi/impostazioni.ts";

export const POSTI_TORNEO = 4;
const ERRORI_NOTI = [
  "tornei_spenti",
  "serve_silver",
  "troppi_tornei",
  "torneo_pieno",
  "torneo_iniziato",
  "gia_dentro",
  "servono_4",
  "non_trovato",
  "non_autenticato",
  "offline",
  "timeout",
  "troppe_richieste",
  "bloccato",
  "troppo_presto",
  "nome_vietato",
];
export const codiceErroreTorneo = (e: unknown): string => {
  const c = e instanceof ErroreRete ? e.codice : "generico";
  return ERRORI_NOTI.includes(c) ? c : "generico";
};

/** Partite già aperte da questa scheda: si entra da soli una volta, poi si torna qui senza essere rimbalzati. */
function giaAperta(partita: string, segna = false): boolean {
  try {
    const chiave = `ld.torneo_partita.${partita}`;
    if (segna) sessionStorage.setItem(chiave, "1");
    return sessionStorage.getItem(chiave) === "1";
  } catch {
    return segna;
  }
}

type Passo =
  | { p: "caricamento" }
  | { p: "invito"; info: InfoTorneo }
  | { p: "dentro"; token: string; vista: VistaTorneo | null }
  | { p: "errore"; codice: string };

export function Torneo({ codice, tema }: { codice: string; tema: ControlloTema }) {
  const { t } = useTranslation();
  const [passo, setPasso] = useState<Passo>(() => {
    const token = tokenTorneo(codice);
    return token ? { p: "dentro", token, vista: null } : { p: "caricamento" };
  });
  const [errore, setErrore] = useState<string | null>(null);
  const [occupato, setOccupato] = useState(false);
  const [conferma, setConferma] = useState(false);
  const account = useAccount();
  const tester = account.tipo === "registrato" && account.io.tester === true;
  // v041: il mini tutorial del torneo già qui, la prima volta (prima arrivava solo all'inizio della prima partita)
  const [tutorial, setTutorial] = useState(false);
  const dentro = passo.p === "dentro" && !!passo.vista;
  useEffect(() => {
    if (dentro && !tutorialVisto("torneo")) setTutorial(true);
  }, [dentro]);

  const leggiInfo = useCallback(() => {
    leggiTorneo(codice)
      .then((info) => setPasso({ p: "invito", info }))
      .catch((e: unknown) => setPasso({ p: "errore", codice: e instanceof ErroreRete && e.stato === 404 ? "non_trovato" : codiceErroreTorneo(e) }));
  }, [codice]);

  useEffect(() => {
    if (passo.p === "caricamento") leggiInfo();
  }, [passo.p, leggiInfo]);

  // Dentro: lo stato ogni pochi secondi (e subito quando si torna sulla pagina); a torneo finito o annullato basta.
  const finito = passo.p === "dentro" && (passo.vista?.stato === "finito" || passo.vista?.stato === "annullato");
  const token = passo.p === "dentro" && !finito ? passo.token : null;
  const ogniMs = Math.max(1, leggiImpostazione(IMP_APP.torneoAggiornaSecondi, TORNEO_AGGIORNA_SECONDI, eNumero)) * 1000;
  const aggiorna = useCallback(async () => {
    if (!token) return;
    try {
      const vista = await statoTorneo(codice, token);
      setPasso((p) => (p.p === "dentro" && p.token === token ? { ...p, vista } : p));
      setErrore(null);
    } catch (e) {
      if (e instanceof ErroreRete && (e.stato === 403 || e.stato === 404)) {
        // Token non più valido (uscito, o torneo cancellato): si torna all'invito.
        dimenticaTorneo(codice);
        setPasso({ p: "caricamento" });
        return;
      }
      setErrore(codiceErroreTorneo(e));
    }
  }, [codice, token]);
  useEffect(() => {
    if (!token) return;
    void aggiorna();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") void aggiorna();
    }, ogniMs);
    const visibile = () => document.visibilityState === "visible" && void aggiorna();
    document.addEventListener("visibilitychange", visibile);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", visibile);
    };
  }, [token, aggiorna, ogniMs]);

  // Quando tocca a me si entra nella partita da soli (una volta per partita).
  const vista = passo.p === "dentro" ? passo.vista : null;
  const mia = vista?.mia ?? null;
  const entraRif = useRef<string | null>(null);
  const gioca = useCallback(
    (m: NonNullable<VistaTorneo["mia"]>) => {
      preparaPartita(codice, m.codice, m.token);
      giaAperta(m.codice, true);
      vai(`${PERCORSO_SFIDA}${m.codice}`);
    },
    [codice],
  );
  useEffect(() => {
    if (!mia || entraRif.current === mia.codice || giaAperta(mia.codice)) return;
    entraRif.current = mia.codice;
    gioca(mia);
  }, [mia, gioca]);

  const azione = async (f: () => Promise<unknown>) => {
    setOccupato(true);
    setErrore(null);
    try {
      await f();
      await aggiorna();
    } catch (e) {
      setErrore(codiceErroreTorneo(e));
    } finally {
      setOccupato(false);
    }
  };

  if (passo.p === "errore") return <Messaggio tema={tema} testo={t(`torneo.errore_${passo.codice}`)} />;
  if (passo.p === "caricamento" || (passo.p === "dentro" && !passo.vista)) {
    return (
      <div className="pagina schermata-semplice" aria-busy="true">
        <Faccina tema={tema.tema} />
        <p className="schermata-testo">{errore ? t(`torneo.errore_${errore}`) : t("generale.caricamento")}</p>
      </div>
    );
  }
  if (passo.p === "invito") return <Invito codice={codice} info={passo.info} tema={tema} onDentro={(tk) => setPasso({ p: "dentro", token: tk, vista: null })} />;

  const v = passo.vista!;
  const nome = (posto: number | null) => (posto === null ? "?" : (v.posti.find((p) => p.posto === posto)?.nome ?? "?"));
  const organizzatore = nome(1);
  const finale = v.incontri.find((i) => i.turno === "finale");
  const eliminato = v.incontri.some((i) => i.vincitore !== null && i.posti.includes(v.io.posto) && i.vincitore !== v.io.posto);

  return (
    <div className="pagina ld-pagina torneo" data-stato={v.stato}>
      {tutorial && <Tutorial tipo="torneo" onFine={() => setTutorial(false)} />}
      <header className="torneo-testa">
        <Palco
          colore="oro"
          compatto
          titolo={t("torneo.titolo", { nome: organizzatore })}
          figura={<Faccina tema={tema.tema} quale={v.posti.find((x) => x.posto === 1)?.avatar ?? (v.io.organizzatore ? "io" : "mistero")} />}
        />
      </header>

      {v.stato === "attesa" && (
        <section className="torneo-attesa" aria-labelledby="torneo-posti">
          <h2 id="torneo-posti" className="pannello-titolo">
            {t("torneo.giocatori", { n: v.posti.length, totale: POSTI_TORNEO })}
          </h2>
          <ol className="torneo-posti">
            {Array.from({ length: POSTI_TORNEO }, (_, i) => i + 1).map((posto) => {
              const p = v.posti.find((x) => x.posto === posto);
              return (
                <li key={posto} className={p ? "posto-preso" : "posto-libero"} data-posto={posto}>
                  {p && <Avatar quale={p.avatar ?? "mistero"} dimensione={30} classe="torneo-posto-avatar" />}
                  {p ? p.nome : t("torneo.posto_libero")}
                  {/* v041: il tester che organizza riempie i posti vuoti con un bot (per provare il torneo da solo) */}
                  {!p && tester && v.io.organizzatore && passo.p === "dentro" && (
                    <PulsanteBot tipo="posto" disabled={occupato} onClick={() => void azione(() => aggiungiBotTorneo(codice, passo.token))} />
                  )}
                  {p && posto === v.io.posto && <span className="torneo-tu"> · {t("gioco.tu")}</span>}
                  {posto === 1 && <span className="torneo-tu"> · {t("torneo.organizza_etichetta")}</span>}
                </li>
              );
            })}
          </ol>
          {v.io.organizzatore ? (
            <>
              {v.posti.length < POSTI_TORNEO && <Condividi link={linkTorneo(codice)} messaggio={t("torneo.messaggio_condividi")} />}
              {v.posti.length < POSTI_TORNEO && <InvitaAmici tipo="torneo" codice={codice} />}
              <div className="azioni-colonna">
                {/* v040: «Inizia» compare quando ci sono tutti (prima era spento) */}
                {v.posti.length >= POSTI_TORNEO && (
                  <button
                    type="button"
                    className="pulsante pulsante-primario pulsante-grande"
                    disabled={occupato}
                    onClick={() => void azione(() => iniziaTorneo(codice, passo.token))}
                  >
                    {t("torneo.inizia")}
                  </button>
                )}
                {v.posti.length < POSTI_TORNEO && <p className="nota-centrata">{t("torneo.servono", { n: POSTI_TORNEO - v.posti.length })}</p>}
                {conferma ? (
                  <div className="azioni-riga" role="alertdialog" aria-label={t("torneo.annulla_conferma")}>
                    <p className="nota">{t("torneo.annulla_conferma")}</p>
                    <button type="button" className="pulsante pulsante-pericolo" disabled={occupato} onClick={() => void azione(() => annullaTorneo(codice, passo.token))}>
                      {t("torneo.annulla")}
                    </button>
                    <button type="button" className="pulsante pulsante-secondario" onClick={() => setConferma(false)}>
                      {t("generale.annulla")}
                    </button>
                  </div>
                ) : (
                  <button type="button" className="pulsante pulsante-piccolo" onClick={() => setConferma(true)}>
                    {t("torneo.annulla")}
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="azioni-colonna">
              <p className="nota-centrata">{t("torneo.aspetta_via", { nome: organizzatore })}</p>
              <button
                type="button"
                className="pulsante pulsante-piccolo"
                disabled={occupato}
                onClick={() =>
                  void esciTorneo(codice, passo.token)
                    .catch(() => undefined)
                    .then(() => {
                      dimenticaTorneo(codice);
                      vai("/");
                    })
                }
              >
                {t("torneo.esci")}
              </button>
            </div>
          )}
        </section>
      )}

      {(v.stato === "semifinali" || v.stato === "finale" || v.stato === "finito") && (
        <section className="tabellone" aria-label={t("torneo.tabellone")}>
          {(["semi1", "semi2", "finale"] as Turno[]).map((turno) => {
            const i = v.incontri.find((x) => x.turno === turno);
            return (
              <div key={turno} className={`incontro incontro-${turno}`} data-turno={turno} data-in-corso={i?.inCorso ? "1" : "0"}>
                <p className="incontro-titolo">{t(`torneo.turno_${turno}`)}</p>
                {i ? (
                  <p className="incontro-nomi">
                    {i.posti.map((posto, k) => (
                      <span key={posto}>
                        {k > 0 && <span className="incontro-contro"> {t("torneo.contro")} </span>}
                        <span className={i.vincitore === posto ? "incontro-vincitore" : i.vincitore !== null ? "incontro-fuori" : ""}>{nome(posto)}</span>
                      </span>
                    ))}
                  </p>
                ) : (
                  <p className="incontro-nomi incontro-attesa">{t("torneo.finale_attesa")}</p>
                )}
                {i?.inCorso && <p className="nota">{t("torneo.in_corso")}</p>}
              </div>
            );
          })}
        </section>
      )}

      {v.stato !== "attesa" && (
        <section className="azioni-colonna torneo-azioni">
          {v.stato === "finito" ? (
            <>
              <p className="pannello-titolo risultato-vinto" role="status">
                {v.vincitore === v.io.posto ? t("torneo.hai_vinto") : t("torneo.ha_vinto", { nome: nome(v.vincitore) })}
              </p>
              {/* v039: chi vince il torneo lo può raccontare */}
              {v.vincitore === v.io.posto && <Condividi link={null} vinto messaggio={t("torneo.messaggio_vittoria")} />}
            </>
          ) : v.stato === "annullato" ? (
            <p className="pannello-titolo" role="status">
              {t("torneo.annullato")}
            </p>
          ) : mia ? (
            <button type="button" className="pulsante pulsante-primario pulsante-grande" onClick={() => gioca(mia)}>
              {t(mia.turno === "finale" ? "torneo.gioca_finale" : "torneo.gioca_semifinale")}
            </button>
          ) : v.guarda ? (
            <>
              <p className="nota-centrata">{t("torneo.eliminato")}</p>
              <button type="button" className="pulsante pulsante-primario pulsante-grande" onClick={() => vai(`${PERCORSO_TORNEO}${codice}/guarda`)}>
                {t("torneo.guarda_finale")}
              </button>
            </>
          ) : eliminato ? (
            <p className="nota-centrata">{v.spettatori && !finale ? t("torneo.eliminato_aspetta") : t("torneo.eliminato")}</p>
          ) : (
            <p className="nota-centrata">{t("torneo.aspetta_altri")}</p>
          )}
          {(v.stato === "finito" || v.stato === "annullato") && (
            <button
              type="button"
              className="pulsante pulsante-secondario"
              onClick={() => {
                dimenticaTorneo(codice);
                vai("/");
              }}
            >
              {t("gioco.torna_home")}
            </button>
          )}
        </section>
      )}
      {errore && (
        <p className="admin-errore" role="alert">
          {t(`torneo.errore_${errore}`)}
        </p>
      )}
      {v.stato !== "finito" && v.stato !== "annullato" && (
        <p className="nota-centrata torneo-scade">{t("torneo.scade", { ora: new Date(v.scadeIl).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) })}</p>
      )}
    </div>
  );
}

function Invito({ codice, info, tema, onDentro }: { codice: string; info: InfoTorneo; tema: ControlloTema; onDentro: (token: string) => void }) {
  const { t } = useTranslation();
  const [nome, setNome] = useNomeGiocatore(); // v039: anche il nome dell'account su un telefono nuovo
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  if (info.stato !== "attesa" || info.posti >= POSTI_TORNEO) {
    const motivo = info.stato === "finito" ? "finito" : info.stato === "annullato" ? "annullato" : info.stato === "attesa" ? "pieno" : "iniziato";
    return <Messaggio tema={tema} testo={t(`torneo.non_si_entra_${motivo}`)} />;
  }
  return (
    <div className="pagina invito">
      <main className="invito-centro">
        <Faccina tema={tema.tema} quale={info.avatarOrganizzatore ?? "mistero"} />
        <h1 className="invito-titolo">{t("torneo.invito_titolo", { nome: info.organizzatore ?? "" })}</h1>
        <p className="invito-testo">{t("torneo.invito_testo", { n: info.posti, totale: POSTI_TORNEO })}</p>
        <form
          className="invito-modulo"
          onSubmit={(e) => {
            e.preventDefault();
            const n = nome.trim();
            if (!n) return;
            salvaPreferenza(PREFERENZE.nome, n);
            setInvio(true);
            setErrore(null);
            entraTorneo(codice, n)
              .then((r) => {
                salvaTokenTorneo(codice, r.token);
                onDentro(r.token);
              })
              .catch((err: unknown) => {
                setErrore(codiceErroreTorneo(err));
                setInvio(false);
              });
          }}
        >
          <CampoNome valore={nome} onCambia={setNome} />
          {errore === "bloccato" ? (
            <AvvisoSospensione seNo={<p className="admin-errore" role="alert">{t("torneo.errore_bloccato")}</p>} />
          ) : errore && (
            <p className="admin-errore" role="alert">
              {t(`torneo.errore_${errore}`)}
            </p>
          )}
          <button type="submit" className="pulsante pulsante-primario" disabled={invio}>
            {t("torneo.entra")}
          </button>
          <button type="button" className="pulsante pulsante-secondario" onClick={() => vai("/")}>
            {t("gioco.no_grazie")}
          </button>
        </form>
      </main>
    </div>
  );
}

function Messaggio({ tema, testo }: { tema: ControlloTema; testo: string }) {
  const { t } = useTranslation();
  return (
    <div className="pagina schermata-semplice">
      <Faccina tema={tema.tema} />
      <p className="schermata-testo" role="alert">
        {testo}
      </p>
      <button type="button" className="pulsante pulsante-secondario" onClick={() => vai("/")}>
        {t("gioco.torna_home")}
      </button>
    </div>
  );
}
